import {
    Box,
    Container,
    Flex,
    Heading,
    Spinner,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Select,
    Input,
    Text,
    useToast,
    Badge,
    HStack,
    Divider,
    Card,
    CardHeader,
    CardBody,
    IconButton,
    Tooltip,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FiClock, FiEye, FiPhoneCall } from 'react-icons/fi';
import React, { useContext, useState, useEffect } from 'react';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import UploadYourFile from './UploadYourFile';
import Loading from '../compnents/Loading';

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

const buildOperationalNotice = (error, fallbackTitle, fallbackDescription) => {
    const status = error.response?.status;
    const backendMessage = error.response?.data?.message;

    if (status === 403) {
        return {
            status: 'info',
            title: 'Workspace step required',
            description: backendMessage || 'Assigning clients to advisers is available on Team and Business, including during a Team trial.',
        };
    }

    if (status === 404) {
        return {
            status: 'info',
            title: fallbackTitle,
            description: fallbackDescription,
        };
    }

    return {
        status: 'warning',
        title: fallbackTitle,
        description: backendMessage || fallbackDescription,
    };
};

function ManageCustomers() {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const toast = useToast();
    const [pageNotice, setPageNotice] = useState(null);

    // Customer Management State
    const [customers, setCustomers] = useState([]);
    const [rms, setRms] = useState([]);
    const [selectedRM, setSelectedRM] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingRMs, setLoadingRMs] = useState(false);
    const [assigningCustomer, setAssigningCustomer] = useState(null);
    const [customerActionStatus, setCustomerActionStatus] = useState({});

    // RM Book State
    const [selectedRMForBook, setSelectedRMForBook] = useState('');
    const [rmBook, setRmBook] = useState(null);
    const [loadingRMBook, setLoadingRMBook] = useState(false);

    // Who To Call State
    const [selectedRMForCalls, setSelectedRMForCalls] = useState('');
    const [whoToCallList, setWhoToCallList] = useState([]);
    const [loadingWhoToCall, setLoadingWhoToCall] = useState(false);

    // Fetch customers and RMs on component mount
    useEffect(() => {
        if (user && token) {
            fetchCustomers();
            fetchRMs();
        }
        // eslint-disable-next-line
    }, [user, token, currentPage, searchQuery]);

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const params = {
                page: currentPage,
                page_size: pageSize,
            };
            if (searchQuery) params.search = searchQuery;

            const res = await axios.get(`${API_BASE_URL}/content/list-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setCustomers(res.data.customers);
            setTotalCount(res.data.pagination.total_count);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
            setTotalCount(0);
            setPageNotice(buildOperationalNotice(
                error,
                'Customer list is not ready yet',
                'Upload and process a dataset before assigning customers to RMs.'
            ));
        } finally {
            setLoadingCustomers(false);
        }
    };

    const fetchRMs = async () => {
        setLoadingRMs(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/content/list-rms`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setRms(res.data.rms);
        } catch (error) {
            console.error('Error fetching RMs:', error);
            setRms([]);
            setPageNotice(buildOperationalNotice(
                error,
                'RM users are not available yet',
                'Create or invite RM users before assigning client books.'
            ));
        } finally {
            setLoadingRMs(false);
        }
    };

    const handleAssignRM = async (customerId, rmId) => {
        setAssigningCustomer(customerId);
        try {
            const endpoint = customers.find(c => c.customer_id === customerId)?.rm_id
                ? '/content/reassign-rm'
                : '/content/assign-rm';

            const payload = customers.find(c => c.customer_id === customerId)?.rm_id
                ? { customer_id: customerId, new_rm_id: rmId }
                : { customer_id: customerId, rm_id: rmId };

            await axios({
                method: customers.find(c => c.customer_id === customerId)?.rm_id ? 'put' : 'post',
                url: `${API_BASE_URL}${endpoint}`,
                headers: { Authorization: `Bearer ${token}` },
                data: payload
            });

            toast({
                title: 'Success',
                description: `RM ${customers.find(c => c.customer_id === customerId)?.rm_id ? 'reassigned' : 'assigned'} successfully`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            fetchCustomers();
        } catch (error) {
            console.error('Error assigning RM:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to assign RM',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setAssigningCustomer(null);
        }
    };

    const fetchRMBook = async (rmId) => {
        if (!rmId) return;

        setLoadingRMBook(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/content/admin-view-rm-book`,
                { rm_id: rmId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setRmBook(res.data);
        } catch (error) {
            console.error('Error fetching RM book:', error);
            setRmBook(null);
            setPageNotice(buildOperationalNotice(
                error,
                'This RM book is empty',
                'Assign customers to the selected RM before opening their book.'
            ));
        } finally {
            setLoadingRMBook(false);
        }
    };

    const fetchWhoToCall = async (rmId) => {
        if (!rmId) return;

        setLoadingWhoToCall(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/content/admin-who-to-call-for-a-rm`,
                { rm_id: rmId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setWhoToCallList(res.data);
        } catch (error) {
            console.error('Error fetching who to call list:', error);
            setWhoToCallList([]);
            setPageNotice(buildOperationalNotice(
                error,
                'Who-to-call list is not ready yet',
                'Complete scoring and assign customers to this RM before generating the priority list.'
            ));
        } finally {
            setLoadingWhoToCall(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const getCustomerOperatingAction = (customer) => {
        const riskProfile = Number(customer.risk_profile) || 0;

        if (!customer.rm_id) {
            return {
                label: 'Assign owner',
                detail: 'Ownership is required before RM follow-up can start.',
                colorScheme: 'orange',
            };
        }

        if (riskProfile >= 8) {
            return {
                label: 'Risk review',
                detail: 'High-risk client. Queue a review before the next RM contact.',
                colorScheme: 'red',
            };
        }

        if (riskProfile >= 5) {
            return {
                label: 'Schedule check-in',
                detail: 'Medium-risk profile. Keep the client in the next follow-up cycle.',
                colorScheme: 'yellow',
            };
        }

        return {
            label: 'Monitor',
            detail: 'Stable client. Keep visible in the book and review on normal cadence.',
            colorScheme: 'green',
        };
    };

    const handleCustomerAction = (customerId, actionLabel) => {
        setCustomerActionStatus((previous) => ({
            ...previous,
            [customerId]: `${actionLabel} queued for demo`,
        }));
    };

    const getUrgencyColor = (urgency) => {
        switch (urgency?.toLowerCase()) {
            case 'high': return 'red';
            case 'medium': return 'orange';
            case 'low': return 'green';
            default: return 'gray';
        }
    };

    if (isAuthInitializing || !token) {
        return <Loading />;
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <UploadYourFile token={token} />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" minW={0} py={8}>
                    <VStack spacing={8} align="stretch">
                        {/* Header */}
                        <Heading size="lg" bgGradient="linear(to-r, brand.navy, brand.tealDark)" bgClip="text">
                            Manage Customers
                        </Heading>

                        {pageNotice ? (
                            <Alert status={pageNotice.status} borderRadius="lg" alignItems="flex-start">
                                <AlertIcon mt={1} />
                                <Box>
                                    <AlertTitle fontSize="sm">{pageNotice.title}</AlertTitle>
                                    <AlertDescription fontSize="sm">{pageNotice.description}</AlertDescription>
                                </Box>
                            </Alert>
                        ) : null}

                        {/* Section 1: Customer Assignment */}
                        <Card>
                            <CardHeader>
                                <Heading size="md">Customer Assignment</Heading>
                            </CardHeader>
                            <CardBody>
                                {/* Search Bar */}
                                <HStack mb={4}>
                                    <Input
                                        placeholder="Search customers..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        leftElement={<SearchIcon color="gray.500" />}
                                    />
                                    <Button onClick={fetchCustomers} colorScheme="blue">
                                        Search
                                    </Button>
                                </HStack>

                                {loadingCustomers ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" />
                                    </Flex>
                                ) : customers.length === 0 ? (
                                    <Text color="gray.500" textAlign="center" py={8}>
                                        Customer records will appear here after a dataset is processed.
                                    </Text>
                                ) : (
                                    <>
                                        <Box overflowX="auto">
                                            <Table variant="simple">
                                                <Thead>
                                                    <Tr>
                                                        <Th>Customer ID</Th>
                                                        <Th>Country</Th>
                                                        <Th>Age</Th>
                                                        <Th>Risk Profile</Th>
                                                        <Th>Recommended CTA</Th>
                                                        <Th>Current RM</Th>
                                                        <Th>Assign/Reassign RM</Th>
                                                        <Th>Action</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {customers.map((customer) => {
                                                        const operatingAction = getCustomerOperatingAction(customer);
                                                        const queuedAction = customerActionStatus[customer.customer_id];

                                                        return (
                                                            <Tr key={customer.customer_id}>
                                                                <Td fontWeight="semibold">{customer.customer_id}</Td>
                                                                <Td>{customer.country_iso}</Td>
                                                                <Td>{customer.age}</Td>
                                                                <Td>
                                                                    <Badge colorScheme={customer.risk_profile > 7 ? 'red' : customer.risk_profile > 4 ? 'orange' : 'green'}>
                                                                        {customer.risk_profile}
                                                                    </Badge>
                                                                </Td>
                                                                <Td minW="220px">
                                                                    <Badge colorScheme={operatingAction.colorScheme} mb={2}>
                                                                        {operatingAction.label}
                                                                    </Badge>
                                                                    <Text fontSize="xs" color="gray.600">
                                                                        {operatingAction.detail}
                                                                    </Text>
                                                                </Td>
                                                                <Td>
                                                                    {customer.rm_id ? (
                                                                        <Badge colorScheme="blue">
                                                                            {rms.find(rm => rm.rm_id === customer.rm_id)?.full_name || customer.rm_id}
                                                                        </Badge>
                                                                    ) : (
                                                                        <Text color="gray.500">Not assigned</Text>
                                                                    )}
                                                                </Td>
                                                                <Td minW="180px">
                                                                    <Select
                                                                        placeholder="Select RM"
                                                                        size="sm"
                                                                        value={selectedRM[customer.customer_id] || ''}
                                                                        onChange={(e) => setSelectedRM({ ...selectedRM, [customer.customer_id]: e.target.value })}
                                                                        isDisabled={loadingRMs || assigningCustomer === customer.customer_id}
                                                                    >
                                                                        {rms.map((rm) => (
                                                                            <option key={rm.rm_id} value={rm.rm_id}>
                                                                                {rm.full_name}
                                                                            </option>
                                                                        ))}
                                                                    </Select>
                                                                </Td>
                                                                <Td minW="280px">
                                                                    <VStack align="stretch" spacing={2}>
                                                                        <HStack spacing={2} flexWrap="wrap">
                                                                            <Button
                                                                                size="xs"
                                                                                colorScheme={customer.rm_id ? 'orange' : 'green'}
                                                                                onClick={() => handleAssignRM(customer.customer_id, selectedRM[customer.customer_id])}
                                                                                isDisabled={!selectedRM[customer.customer_id] || assigningCustomer === customer.customer_id}
                                                                                isLoading={assigningCustomer === customer.customer_id}
                                                                            >
                                                                                {customer.rm_id ? 'Reassign' : 'Assign'}
                                                                            </Button>
                                                                            <Button
                                                                                size="xs"
                                                                                variant="outline"
                                                                                colorScheme="blue"
                                                                                leftIcon={<FiPhoneCall />}
                                                                                onClick={() => handleCustomerAction(customer.customer_id, 'Call')}
                                                                            >
                                                                                Call
                                                                            </Button>
                                                                            <Button
                                                                                size="xs"
                                                                                variant="outline"
                                                                                colorScheme="teal"
                                                                                leftIcon={<FiEye />}
                                                                                onClick={() => handleCustomerAction(customer.customer_id, 'Review')}
                                                                            >
                                                                                Review
                                                                            </Button>
                                                                            <Button
                                                                                size="xs"
                                                                                variant="ghost"
                                                                                colorScheme="gray"
                                                                                leftIcon={<FiClock />}
                                                                                onClick={() => handleCustomerAction(customer.customer_id, 'Snooze')}
                                                                            >
                                                                                Snooze
                                                                            </Button>
                                                                        </HStack>
                                                                        {queuedAction ? (
                                                                            <Badge alignSelf="flex-start" colorScheme="blue" textTransform="none">
                                                                                {queuedAction}
                                                                            </Badge>
                                                                        ) : null}
                                                                    </VStack>
                                                                </Td>
                                                            </Tr>
                                                        );
                                                    })}
                                                </Tbody>
                                            </Table>
                                        </Box>

                                        {/* Pagination */}
                                        <Flex justify="space-between" align="center" mt={4}>
                                            <Text color="gray.600">
                                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} customers
                                            </Text>
                                            <HStack>
                                                <IconButton
                                                    icon={<ChevronLeftIcon />}
                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                    isDisabled={currentPage === 1}
                                                    aria-label="Previous page"
                                                />
                                                <Text fontWeight="semibold">
                                                    Page {currentPage} of {totalPages}
                                                </Text>
                                                <IconButton
                                                    icon={<ChevronRightIcon />}
                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                    isDisabled={currentPage === totalPages}
                                                    aria-label="Next page"
                                                />
                                            </HStack>
                                        </Flex>
                                    </>
                                )}
                            </CardBody>
                        </Card>

                        <Divider />

                        {/* Section 2: RM Book View */}
                        <Card>
                            <CardHeader>
                                <Heading size="md">View RM Customer Book</Heading>
                            </CardHeader>
                            <CardBody>
                                <HStack mb={4}>
                                    <Select
                                        placeholder="Select RM to view book"
                                        value={selectedRMForBook}
                                        onChange={(e) => setSelectedRMForBook(e.target.value)}
                                    >
                                        {rms.map((rm) => (
                                            <option key={rm.rm_id} value={rm.rm_id}>
                                                {rm.full_name}
                                            </option>
                                        ))}
                                    </Select>
                                    <Button
                                        colorScheme="teal"
                                        onClick={() => fetchRMBook(selectedRMForBook)}
                                        isDisabled={!selectedRMForBook}
                                        isLoading={loadingRMBook}
                                    >
                                        View Book
                                    </Button>
                                </HStack>

                                {loadingRMBook ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" />
                                    </Flex>
                                ) : rmBook ? (
                                    <Box>
                                        <Alert status="info" mb={4}>
                                            <AlertIcon />
                                            {rmBook.rm_name} has {rmBook.customer_count} customer(s)
                                        </Alert>
                                        <Box overflowX="auto">
                                            <Table variant="simple" size="sm">
                                                <Thead>
                                                    <Tr>
                                                        <Th>Customer ID</Th>
                                                        <Th>Country</Th>
                                                        <Th>Age</Th>
                                                        <Th>Risk Profile</Th>
                                                        <Th>Created At</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {rmBook.customers?.map((customer) => (
                                                        <Tr key={customer.customer_id}>
                                                            <Td fontWeight="semibold">{customer.customer_id}</Td>
                                                            <Td>{customer.country_iso}</Td>
                                                            <Td>{customer.age}</Td>
                                                            <Td>
                                                                <Badge colorScheme={customer.risk_profile > 7 ? 'red' : customer.risk_profile > 4 ? 'orange' : 'green'}>
                                                                    {customer.risk_profile}
                                                                </Badge>
                                                            </Td>
                                                            <Td>{new Date(customer.created_at).toLocaleDateString()}</Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Text color="gray.500" textAlign="center" py={8}>
                                        Select an RM to view their customer book
                                    </Text>
                                )}
                            </CardBody>
                        </Card>

                        <Divider />

                        {/* Section 3: Who To Call */}
                        <Card>
                            <CardHeader>
                                <Heading size="md">Who To Call (Priority List)</Heading>
                            </CardHeader>
                            <CardBody>
                                <HStack mb={4}>
                                    <Select
                                        placeholder="Select RM for who-to-call list"
                                        value={selectedRMForCalls}
                                        onChange={(e) => setSelectedRMForCalls(e.target.value)}
                                    >
                                        {rms.map((rm) => (
                                            <option key={rm.rm_id} value={rm.rm_id}>
                                                {rm.full_name}
                                            </option>
                                        ))}
                                    </Select>
                                    <Button
                                        colorScheme="teal"
                                        onClick={() => fetchWhoToCall(selectedRMForCalls)}
                                        isDisabled={!selectedRMForCalls}
                                        isLoading={loadingWhoToCall}
                                    >
                                        Get List
                                    </Button>
                                </HStack>

                                {loadingWhoToCall ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" />
                                    </Flex>
                                ) : whoToCallList.length > 0 ? (
                                    <Box overflowX="auto">
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th>Customer ID</Th>
                                                    <Th>Priority Score</Th>
                                                    <Th>Urgency</Th>
                                                    <Th>Risk Profile</Th>
                                                    <Th>Age</Th>
                                                    <Th>Country</Th>
                                                    <Th>Recommended Action</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {whoToCallList.map((item) => (
                                                    <Tr key={item.customer_id}>
                                                        <Td fontWeight="semibold">{item.customer_id}</Td>
                                                        <Td>
                                                            <Tooltip label={`Priority: ${item.priority_score}`}>
                                                                <Badge colorScheme="teal" fontSize="md">
                                                                    {item.priority_score}
                                                                </Badge>
                                                            </Tooltip>
                                                        </Td>
                                                        <Td>
                                                            <Badge colorScheme={getUrgencyColor(item.urgency_level)}>
                                                                {item.urgency_level}
                                                            </Badge>
                                                        </Td>
                                                        <Td>
                                                            <Badge colorScheme={item.risk_profile > 7 ? 'red' : item.risk_profile > 4 ? 'orange' : 'green'}>
                                                                {item.risk_profile}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{item.age}</Td>
                                                        <Td>{item.country_iso}</Td>
                                                        <Td maxW="200px">
                                                            <Text fontSize="sm" isTruncated>
                                                                {item.recommended_action}
                                                            </Text>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                ) : (
                                    <Text color="gray.500" textAlign="center" py={8}>
                                        Select an RM to view their priority call list
                                    </Text>
                                )}
                            </CardBody>
                        </Card>
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
}

export default ManageCustomers;
